import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { CyberTopology, CyberTopologyNode } from '../../../models/cyber-topology.model';
import { CyberEdgeImpact, CyberState, edgeImpacts, edgeKey } from '../state/cyber-state';
import {
  LEAF_R,
  NODE_LINE_H,
  NODE_SUB_GAP,
  VIEW_H,
  VIEW_W,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
  nodeHeight,
  nodeWidth,
  topologyTransform,
  wrapNodeLabel,
} from '../state/topology-layout';

/**
 * Renders a user-authored network topology and colours it from the derived state.
 *
 * The diagram itself knows nothing about attacks: a node is flagged because an alert named
 * it, and a link reads as compromised because both its ends are flagged. Nodes of kind
 * `leaf` are drawn as terminal devices hanging off their parent rather than as boxes.
 */

const SPARK_W = 8;
const SPARK_H = 2.6;
const SPARK_POINTS = 24;

/** Vertical gaps in the stack under a node: subtitle, then the sparkline, then its latest value. */
const SPARK_GAP = 1.5;
const METRIC_GAP = 1.9;

interface RenderedNode {
  id: string;
  /** The label as authored, shown in the node's `<title>`. */
  label: string;
  /** The label broken into the lines the box draws. */
  lines: string[];
  /** Baseline of the first line, so the block sits centred however many lines there are. */
  firstLineY: number;
  sub: string | null;
  kind: string;
  cx: number;
  cy: number;
  x: number;
  y: number;
  /** Box size, fitted to the label in both directions. */
  w: number;
  h: number;
  /** Baselines below the box, which move with its height. */
  subY: number;
  metricY: number;
  flagged: boolean;
  /** Named by the alert the operator has open — what that one alert is about. */
  selected: boolean;
  /** Polyline points for the traffic sparkline, or null when no alert quoted numbers. */
  spark: string | null;
  latest: number | null;
}

interface RenderedEdge {
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  proto: string | null;
  /** Null when no open alert touches either end. */
  impact: CyberEdgeImpact | null;
}

interface RenderedLeaf {
  key: string;
  label: string;
  cx: number;
  cy: number;
  px: number;
  py: number;
  flagged: boolean;
  selected: boolean;
}

@Component({
  selector: 'app-network-topology',
  standalone: true,
  imports: [],
  templateUrl: './network-topology.html',
  styleUrl: './network-topology.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetworkTopologyComponent {
  topology = input.required<CyberTopology | null>();
  state = input.required<CyberState>();

  /**
   * Node keys the open alert names, ringed so one alert can be told from the rest.
   *
   * Optional: the diagram is meaningful without a selection, and says the same thing it always did.
   */
  selected = input<ReadonlySet<string>>(new Set<string>());

  readonly viewBox = `0 0 ${VIEW_W} ${VIEW_H}`;
  readonly leafR = LEAF_R;
  readonly lineH = NODE_LINE_H;

  zoom = signal(ZOOM_MIN);
  canZoomIn = computed(() => this.zoom() < ZOOM_MAX);
  canZoomOut = computed(() => this.zoom() > ZOOM_MIN);

  zoomIn(): void {
    this.zoom.update((z) => Math.min(ZOOM_MAX, z * ZOOM_STEP));
  }

  zoomOut(): void {
    this.zoom.update((z) => Math.max(ZOOM_MIN, z / ZOOM_STEP));
  }

  resetZoom(): void {
    this.zoom.set(ZOOM_MIN);
  }

  private allNodes = computed<CyberTopologyNode[]>(() => this.topology()?.nodes ?? []);
  private transform = computed(() => topologyTransform(this.allNodes()));
  private impacts = computed(() => edgeImpacts(this.topology(), this.state()));

  nodes = computed<RenderedNode[]>(() => {
    const state = this.state();
    const selected = this.selected();
    const { tx, ty } = this.transform();

    return this.allNodes()
      .filter((node) => node.kind !== 'leaf')
      .map((node) => {
        const cx = tx(node.x);
        const cy = ty(node.y);
        const w = nodeWidth(node.label);
        const h = nodeHeight(node.label);
        const lines = wrapNodeLabel(node.label);
        const subY = cy + h / 2 + NODE_SUB_GAP;
        const sparkBottom = subY + SPARK_H + SPARK_GAP;
        const history = state.nodeTraffic[node.nodeKey] ?? [];
        return {
          id: node.nodeKey,
          label: node.label,
          lines,
          // The block is centred on the box, so a single-line node keeps the baseline it has today.
          firstLineY: cy + 0.2 - ((lines.length - 1) * NODE_LINE_H) / 2,
          sub: node.sub,
          kind: node.kind,
          cx,
          cy,
          x: cx - w / 2,
          y: cy - h / 2,
          w,
          h,
          subY,
          metricY: sparkBottom + METRIC_GAP,
          flagged: state.flaggedNodes.has(node.nodeKey),
          selected: selected.has(node.nodeKey),
          spark: sparkline(history, cx, sparkBottom),
          latest: history.length > 0 ? history[history.length - 1] : null,
        };
      });
  });

  edges = computed<RenderedEdge[]>(() => {
    const { tx, ty } = this.transform();
    const impacts = this.impacts();
    const byKey = new Map(this.allNodes().map((n) => [n.nodeKey, n]));

    return (this.topology()?.edges ?? [])
      .filter((edge) => byKey.has(edge.sourceKey) && byKey.has(edge.targetKey))
      .map((edge) => {
        const a = byKey.get(edge.sourceKey) as CyberTopologyNode;
        const b = byKey.get(edge.targetKey) as CyberTopologyNode;
        return {
          key: edgeKey(edge.sourceKey, edge.targetKey),
          x1: tx(a.x),
          y1: ty(a.y),
          x2: tx(b.x),
          y2: ty(b.y),
          proto: edge.proto,
          impact: impacts.get(edgeKey(edge.sourceKey, edge.targetKey)) ?? null,
        };
      });
  });

  leaves = computed<RenderedLeaf[]>(() => {
    const state = this.state();
    const selected = this.selected();
    const { tx, ty } = this.transform();
    const byKey = new Map(this.allNodes().map((n) => [n.nodeKey, n]));

    return this.allNodes()
      .filter((node) => node.kind === 'leaf')
      .filter((node) => node.parentKey !== null && byKey.has(node.parentKey))
      .map((node) => {
        const parent = byKey.get(node.parentKey as string) as CyberTopologyNode;
        return {
          key: node.nodeKey,
          label: node.label,
          cx: tx(node.x),
          cy: ty(node.y),
          px: tx(parent.x),
          py: ty(parent.y),
          flagged: state.flaggedNodes.has(node.nodeKey),
          selected: selected.has(node.nodeKey),
        };
      });
  });

  isEmpty = computed(() => this.allNodes().length === 0);
}

/** Last few metric values as a polyline under the node, scaled to its own range. */
function sparkline(history: number[], cx: number, bottom: number): string | null {
  const values = history.slice(-SPARK_POINTS);
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const left = cx - SPARK_W / 2;

  return values
    .map((value, i) => {
      const x = left + (i / (values.length - 1)) * SPARK_W;
      const y = bottom - ((value - min) / span) * SPARK_H;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}
